import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Lock, Mail, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import AuthLayout from '../auth/AuthLayout';
import useAxiosClient from '../axiosClient';

/**
 * AppSumo OAuth landing page — the "OAuth redirect URL" registered in the
 * AppSumo Partner Portal (https://manage.zonstaff.com/appsumo).
 *
 * AppSumo sends the buyer here with a single-use ?code=. Flow:
 *   1. POST /appsumo/exchange { code }  → server trades it for the license key.
 *   2a. Already redeemed  → server returns a token; log in and go to the dashboard.
 *   2b. First redemption  → collect email + password (AppSumo never sends us an
 *       email address) and POST /appsumo/register with the signed activation
 *       token, which creates the account and grants the lifetime plan.
 *
 * The code is single-use, so the exchange must fire exactly once — React 18
 * StrictMode double-invokes effects in dev, hence the ref guard.
 */
const AppsumoActivate = ({ onLogin }) => {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const exchangeStarted = useRef(false);

  // 'exchanging' | 'register' | 'submitting' | 'done' | 'error'
  const [phase, setPhase] = useState('exchanging');
  const [error, setError] = useState('');
  const [license, setLicense] = useState(null);
  const [activationToken, setActivationToken] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setPhase('error');
      setError('This page is opened from AppSumo after you activate your purchase. No activation code was found in the link.');
      return;
    }

    (async () => {
      try {
        const { data } = await axiosClient.post('/appsumo/exchange', { code });

        if (data.status === 'linked' && data.token) {
          // Existing account — sign straight in.
          if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
          setPhase('done');
          onLogin?.(data.token, { planId: data.user?.planId, isFreemium: data.user?.isFreemium });
          navigate('/', { replace: true });
          return;
        }

        setLicense(data.license || null);
        setActivationToken(data.activation_token || '');
        setPhase('register');
      } catch (err) {
        setPhase('error');
        setError(
          err.response?.data?.message ||
            'We could not verify your AppSumo purchase. Please return to AppSumo and click "Activate now" again.'
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPhase('submitting');

    try {
      const { data } = await axiosClient.post('/appsumo/register', {
        activation_token: activationToken,
        ...form,
      });

      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      setPhase('done');
      onLogin?.(data.token, { planId: data.user?.planId, isFreemium: data.user?.isFreemium });
      navigate('/', { replace: true });
    } catch (err) {
      setPhase('register');
      setError(err.response?.data?.message || 'Could not activate your license. Please try again.');
    }
  };

  if (phase === 'exchanging' || phase === 'done') {
    return (
      <AuthLayout title="Activating your AppSumo deal" subtitle="Verifying your purchase with AppSumo…">
        <div className="auth-form" style={{ textAlign: 'center', padding: '24px 0' }}>
          <div className="animate-pulse" style={{ fontSize: 15, opacity: 0.8 }}>
            Please wait — this only takes a moment.
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'error') {
    return (
      <AuthLayout title="We couldn't activate your deal" subtitle="Your purchase could not be verified">
        <div className="auth-form">
          <div className="auth-error" role="alert">
            <AlertCircle size={16} style={{ flexShrink: 0, marginRight: 6 }} />
            {error}
          </div>
          <p style={{ fontSize: 13, opacity: 0.75, marginTop: 12 }}>
            Still stuck? Email <strong>support@zonstaff.com</strong> with your AppSumo license key and
            we'll activate it manually.
          </p>
        </div>
      </AuthLayout>
    );
  }

  // phase === 'register' | 'submitting'
  const busy = phase === 'submitting';

  return (
    <AuthLayout
      title="Welcome, Sumo-ling! 🎉"
      subtitle="Create your ZonStaff account to activate your lifetime deal"
    >
      <form onSubmit={handleSubmit} className="auth-form">
        {license?.tier ? (
          <div
            className="auth-divider"
            style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 4 }}
          >
            <CheckCircle2 size={16} />
            <span>Lifetime Tier {license.tier} verified</span>
          </div>
        ) : null}

        {error ? (
          <div className="auth-error" role="alert">
            <AlertCircle size={16} style={{ flexShrink: 0, marginRight: 6 }} />
            {error}
          </div>
        ) : null}

        <div className="auth-row">
          <div className="auth-field">
            <label htmlFor="firstName" className="auth-label">First name</label>
            <div className="auth-input-wrap">
              <User size={18} className="auth-input-icon" />
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={handleChange}
                className="auth-input"
                placeholder="Jane"
                autoComplete="given-name"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="lastName" className="auth-label">Last name</label>
            <div className="auth-input-wrap">
              <User size={18} className="auth-input-icon" />
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={handleChange}
                className="auth-input"
                placeholder="Doe"
                autoComplete="family-name"
              />
            </div>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="companyName" className="auth-label">Business name</label>
          <div className="auth-input-wrap">
            <Building2 size={18} className="auth-input-icon" />
            <input
              id="companyName"
              name="companyName"
              type="text"
              value={form.companyName}
              onChange={handleChange}
              className="auth-input"
              placeholder="Acme Coffee"
              autoComplete="organization"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">Email</label>
          <div className="auth-input-wrap">
            <Mail size={18} className="auth-input-icon" />
            <input
              id="email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              className="auth-input"
              placeholder="you@business.com"
              autoComplete="email"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="password" className="auth-label">Password</label>
          <div className="auth-input-wrap">
            <Lock size={18} className="auth-input-icon" />
            <input
              id="password"
              name="password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={handleChange}
              className="auth-input"
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <p style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
            Already have a ZonStaff account? Enter its email and password to attach this deal to it.
          </p>
        </div>

        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? 'Activating…' : 'Activate my lifetime deal'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default AppsumoActivate;
